import React, { type ReactNode, useState } from "react";
import { usePath } from "./router";
import { useAuth } from "./context/AuthContext";
import { useDrawer } from "./context/DrawerContext";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import ContactDrawer from "./components/ContactDrawer";
import Login from "./pages/Login";
import OrganizationSelection from "./pages/OrganizationSelection";
import Kanban from "./pages/Kanban";
import Tasks from "./pages/Tasks";
import Agenda from "./pages/Agenda";
import Leads from "./pages/Leads";
import Dashboard from "./pages/Dashboard";
import Inbox from "./pages/Inbox";
import Placeholder from "./pages/Placeholder";
import Ventas from "./pages/Ventas";
import Economia from "./pages/Economia";
import InventoryOperations from "./pages/InventoryOperations";
import Retencion from "./pages/Retencion";
import Incidencias from "./pages/Incidencias";
import Postventa from "./pages/Postventa";
import Ejecutivo from "./pages/Ejecutivo";
import ClinicalRecord from "./pages/ClinicalRecord";
import TreatmentTemplates from "./pages/TreatmentTemplates";
import Configuration from "./pages/Configuration";

function RestrictedAccess() {
  return (
    <div className="leads-page">
      <div className="kanban-header">
        <div>
          <h1>Acceso restringido</h1>
          <p className="meta">
            Tu usuario no tiene permiso para acceder a esta sección.
          </p>
        </div>
      </div>
    </div>
  );
}


function screenForPath(
  path: string,
  hasPermission: (code: string) => boolean
): { key: string; node: ReactNode } {

  const guarded = (
    key: string,
    permission: string,
    node: ReactNode
  ) => ({
    key,
    node: hasPermission(permission)
      ? node
      : <RestrictedAccess />,
  });

  if (path.match(/^\/pacientes\/[^/]+\/historia/)) {
    return guarded(
      "leads",
      "clinical.view",
      <ClinicalRecord />
    );
  }

  if (path.startsWith("/plantillas-tratamiento")) {
    return guarded(
      "plantillas-tratamiento",
      "treatment.templates.view",
      <TreatmentTemplates />
    );
  }

  if (path.startsWith("/tareas")) {
    return guarded("tareas", "tasks.view", <Tasks />);
  }

  if (path.startsWith("/leads")) {
    return guarded("leads", "contacts.view", <Leads />);
  }

  if (path.startsWith("/inbox")) {
    return guarded("inbox", "inbox.view", <Inbox />);
  }

  if (path.startsWith("/dashboard")) {
    return guarded(
      "dashboard",
      "dashboard.view",
      <Dashboard />
    );
  }

  if (path.startsWith("/ejecutivo")) {
    return guarded(
      "ejecutivo",
      "executive.view",
      <Ejecutivo />
    );
  }

  if (path.startsWith("/ventas")) {
    return guarded("ventas", "sales.view", <Ventas />);
  }

  if (path.startsWith("/inventario")) {
    return guarded("inventario", "inventory.view", <InventoryOperations />);
  }

  if (path.startsWith("/economia")) {
    return guarded(
      "economia",
      "economics.view",
      <Economia />
    );
  }

  if (path.startsWith("/retencion")) {
    return guarded(
      "retencion",
      "retention.view",
      <Retencion />
    );
  }

  if (path.startsWith("/incidencias")) {
    return guarded(
      "incidencias",
      "issues.view",
      <Incidencias />
    );
  }

  if (path.startsWith("/postventa")) {
    return guarded(
      "postventa",
      "postcare.view",
      <Postventa />
    );
  }

  if (path.startsWith("/agenda")) {
    return guarded(
      "agenda",
      "agenda.view",
      <Agenda />
    );
  }

  if (path.startsWith("/reportes")) {
    return guarded(
      "reportes",
      "reports.view",
      <Placeholder title="Reportes" />
    );
  }

  if (path.startsWith("/configuracion")) {
    return guarded(
      "configuracion",
      "configuration.view",
      <Configuration />
    );
  }

  return guarded("kanban", "kanban.view", <Kanban />);
}

export default function App() {
  const path = usePath();
  const {
    user,
    selectionSession,
    hasPermission,
    permissionsLoading,
  } = useAuth();
  const { contactId, close } = useDrawer();
  // Sidebar renders as a fixed off-canvas drawer below the `.side` media
  // query breakpoint in styles.css (900px); on desktop it's always visible
  // and this state is simply unused (the ☰ toggle itself is hidden there).
  const [navOpen, setNavOpen] = useState(false);

  if (selectionSession) {
    return <OrganizationSelection />;
  }

  if (!user) {
    return <Login />;
  }

  if (permissionsLoading) {
    return (
      <div className="app-shell">
        <div className="app-main">
          <div className="app-content">
            <div className="loading-strip">Cargando permisos...</div>
          </div>
        </div>
      </div>
    );
  }

  const { key, node } = screenForPath(path, hasPermission);

  return (
    <div className="app-shell">
      <Sidebar active={key} open={navOpen} onClose={() => setNavOpen(false)} />
      <div className="app-main">
        <TopBar onMenuClick={() => setNavOpen(true)} />
        <div className="app-content">{node}</div>
      </div>
      {contactId && <ContactDrawer contactId={contactId} onClose={close} />}
    </div>
  );
}
